import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

const TAP_SLOP_PX = 8;

// Mirrors the widths previously generated server-side in snippets/card-gallery.liquid
const LAZY_GALLERY_SRCSET_WIDTHS = [200, 280, 360, 470, 560];

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];
  #suppressHoverResetUntil = 0;
  #hoverPreviewActivated = false;
  #galleryInteractionStarted = false;
  #lazyGalleryUpgradeStarted = false;
  #decodeLockActive = false;
  /** @type {number | null} */
  #decodeLockTimeoutId = null;
  /** @type {{ pointerId: number, startX: number, startY: number } | null} */
  #galleryTapIntent = null;

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);
    this.addEventListener('click', this.#handleSlideshowControlClick, { capture: true });
    this.addEventListener('pointerdown', this.#handleSlideshowControlPointerDown);
    this.addEventListener('pointerover', this.#handleGalleryPointerEnter);
    this.addEventListener('mouseover', this.#handleGalleryPointerEnter);
    this.addEventListener('mouseleave', this.#handleCardMouseLeave);
    this.refs.cardGallery?.addEventListener('pointerdown', this.#handleGalleryTapPointerDown);
    this.refs.cardGallery?.addEventListener('pointerup', this.#handleGalleryTapPointerUp);
    this.refs.cardGallery?.addEventListener('pointercancel', this.#clearGalleryTapIntent);

    // Preload hover images on first interaction for fast response
    // Use pointerenter/mouseenter for desktop and touchstart for mobile.
    this.addEventListener('pointerenter', this.#handleFirstHover, { once: true });
    this.addEventListener('mouseenter', this.#handleFirstHover, { once: true });
    this.addEventListener('touchstart', this.#handleFirstHover, { once: true, passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener('click', this.#handleSlideshowControlClick, { capture: true });
    this.removeEventListener('pointerdown', this.#handleSlideshowControlPointerDown);
    this.removeEventListener('pointerover', this.#handleGalleryPointerEnter);
    this.removeEventListener('mouseover', this.#handleGalleryPointerEnter);
    this.removeEventListener('mouseleave', this.#handleCardMouseLeave);
    this.refs.cardGallery?.removeEventListener('pointerdown', this.#handleGalleryTapPointerDown);
    this.refs.cardGallery?.removeEventListener('pointerup', this.#handleGalleryTapPointerUp);
    this.refs.cardGallery?.removeEventListener('pointercancel', this.#clearGalleryTapIntent);
    this.removeEventListener('pointerenter', this.#handleFirstHover);
    this.removeEventListener('mouseenter', this.#handleFirstHover);
    this.removeEventListener('touchstart', this.#handleFirstHover);
  }

  /**
   * Preloads hover images for smoother slideshow transitions.
   * Preloads next 1 image from current position (reduced from 2 for memory).
   */
  #preloadHoverImages() {
    if (!this.refs.slideshow?.isNested) return;
    if (this.#mobileStaticGalleryActive) return;

    const slides = this.refs.slideshow?.slides || [];
    const currentIndex = this.refs.slideshow?.current || 0;

    // Preload next 1 image for smoother hover (memory-optimized)
    for (let i = 1; i <= 1; i++) {
      const nextSlide = slides[currentIndex + i];
      if (nextSlide) {
        const img = nextSlide.querySelector('img');
        if (img) {
          const srcset = img.getAttribute('srcset');
          if (srcset) {
            this.#preloadImageFromSrcset(srcset);
          } else if (img.currentSrc) {
            this.#preloadImageUrl(img.currentSrc);
          } else if (img.getAttribute('src')) {
            this.#preloadImageUrl(img.getAttribute('src'));
          }

          img.removeAttribute('loading');
          img.removeAttribute('fetchpriority');
          img.setAttribute('decoding', 'async');
        }
      }
    }
  }

  /**
   * Preloads the next preview image after current slide.
   */
  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    const nextImg = currentSlide?.nextElementSibling?.querySelector('img');
    if (nextImg) {
      const srcset = nextImg.getAttribute('srcset');
      if (srcset) {
        this.#preloadImageFromSrcset(srcset);
      } else if (nextImg.currentSrc) {
        this.#preloadImageUrl(nextImg.currentSrc);
      } else if (nextImg.getAttribute('src')) {
        this.#preloadImageUrl(nextImg.getAttribute('src'));
      }

      nextImg.removeAttribute('loading');
      nextImg.removeAttribute('fetchpriority');
      nextImg.setAttribute('decoding', 'async');
    }
  }

  #handleFirstHover = () => {
    this.#preloadHoverImages();
    this.#preloadLazyGalleryInitialImage();
    this.#prefetchProductPage();
  };

  /**
   * Refs can be populated after connectedCallback in Safari depending on custom element upgrade timing.
   * Use DOM fallbacks for gallery work so lazy hover initialization is not lost.
   */
  #getCardGallery() {
    if (this.refs.cardGallery instanceof HTMLElement) return this.refs.cardGallery;

    const cardGallery = this.querySelector('[ref="cardGallery"]');
    return cardGallery instanceof HTMLElement ? cardGallery : null;
  }

  #getCardGalleryLink() {
    if (this.refs.cardGalleryLink instanceof HTMLElement) return this.refs.cardGalleryLink;

    const cardGalleryLink = this.querySelector('[ref="cardGalleryLink"]');
    return cardGalleryLink instanceof HTMLElement ? cardGalleryLink : null;
  }

  /**
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #getEventCardGallery(event) {
    if (!(event.target instanceof Element)) return null;

    const cardGallery = event.target.closest('[ref="cardGallery"]');
    if (!(cardGallery instanceof HTMLElement)) return null;
    if (!this.contains(cardGallery)) return null;

    return cardGallery;
  }

  /**
   * Parses the lazy gallery payload ({ shared, slides }) rendered by card-gallery.liquid.
   * Returns null when there is nothing to upgrade (missing, malformed, or fewer than 2 slides).
   * @param {HTMLElement | null} cardGallery
   * @returns {{ shared: { className?: string, mediaFit?: string, sizes?: string }, slides: Array<any> } | null}
   */
  #parseLazyGalleryData(cardGallery) {
    const dataScript = cardGallery?.querySelector('script[data-card-gallery-slides]');
    if (!(dataScript instanceof HTMLScriptElement)) return null;

    let data;
    try {
      data = JSON.parse(dataScript.textContent || 'null');
    } catch (error) {
      return null;
    }

    if (!data || !Array.isArray(data.slides) || data.slides.length < 2) return null;

    return { shared: data.shared || {}, slides: data.slides };
  }

  /**
   * Builds the srcset client-side from the base image URL by swapping the width parameter.
   * Replaces the full srcset string previously embedded in the JSON payload.
   * @param {string} src
   * @returns {string}
   */
  #buildLazyGallerySrcset(src) {
    if (!src) return '';

    return LAZY_GALLERY_SRCSET_WIDTHS.map((width) => `${this.#setImageUrlWidth(src, width)} ${width}w`).join(', ');
  }

  /**
   * @param {string} src
   * @param {number} width
   * @returns {string}
   */
  #setImageUrlWidth(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', `${width}`);
      return url.toString();
    } catch (error) {
      return src;
    }
  }

  #preloadLazyGalleryInitialImage() {
    if (this.#mobileStaticGalleryActive) return;

    const galleryData = this.#parseLazyGalleryData(this.#getCardGallery());
    if (!galleryData) return;

    const initialSlideData = this.#getLazyGalleryInitialSlide(galleryData.slides);
    if (initialSlideData?.src) {
      this.#preloadImageFromSrcset(this.#buildLazyGallerySrcset(initialSlideData.src));
    }

    this.#loadSlideshowComponent();
  }

  #ensureLazyGallerySlideshow() {
    if (this.#mobileStaticGalleryActive) {
      return null;
    }

    const cardGallery = this.#getCardGallery();
    if (!cardGallery) {
      return null;
    }

    const existingSlideshow = cardGallery.querySelector('slideshow-component');
    if (existingSlideshow) {
      this.refs.slideshow = /** @type {import('slideshow').Slideshow} */ (existingSlideshow);
      return this.refs.slideshow;
    }

    if (this.#lazyGalleryUpgradeStarted) {
      return this.refs.slideshow ?? null;
    }

    const galleryLink = this.#getCardGalleryLink();
    const galleryData = this.#parseLazyGalleryData(cardGallery);
    if (!(galleryLink instanceof HTMLElement) || !galleryData) {
      return null;
    }

    const { shared, slides: slidesData } = galleryData;

    this.#lazyGalleryUpgradeStarted = true;

    const mainSlideData = this.#getLazyGalleryMainSlide(slidesData);
    const initialSlideData = this.#getLazyGalleryInitialSlide(slidesData);
    const orderedSlidesData = this.#getLazyGalleryHoverOrder(slidesData, initialSlideData, mainSlideData);
    const initialSlideIndex = 0;
    const initialSlideId = initialSlideData?.id != null ? `${initialSlideData.id}` : '';
    const mainSlideId = mainSlideData?.id != null ? `${mainSlideData.id}` : '';

    const slideshow = document.createElement('slideshow-component');
    slideshow.setAttribute('ref', 'slideshow');
    slideshow.setAttribute('data-card-gallery-lazy-slideshow', '');
    slideshow.setAttribute('initial-slide', `${initialSlideIndex}`);
    slideshow.setAttribute('infinite', '');
    slideshow.setAttribute('slide-step', '1');
    slideshow.style.setProperty(
      '--slideshow-timeline',
      orderedSlidesData.map((slide) => `--slide-${slide.lazyIndex}`).join(', ')
    );
    if (mainSlideId) slideshow.dataset.mainSlideId = mainSlideId;
    if (initialSlideId) cardGallery.dataset.hoverPreviewSlideId = initialSlideId;
    cardGallery.setAttribute('data-lazy-gallery-loading', '');
    cardGallery.removeAttribute('data-lazy-gallery-ready');

    const container = document.createElement('slideshow-container');
    container.setAttribute('ref', 'slideshowContainer');

    const visibleSlideCount = slidesData.filter((slide) => !slide.hidden).length;
    if (visibleSlideCount > 1) {
      container.appendChild(this.#createLazyGalleryArrows(cardGallery));
    }

    const slides = document.createElement('slideshow-slides');
    slides.setAttribute('tabindex', '-1');
    slides.setAttribute('ref', 'scroller');

    for (const slideData of orderedSlidesData) {
      slides.appendChild(this.#createLazyGallerySlide(slideData, shared, initialSlideId, mainSlideId));
    }

    container.appendChild(slides);
    slideshow.appendChild(container);
    galleryLink.appendChild(slideshow);
    cardGallery.querySelector('script[data-card-gallery-slides]')?.remove();

    cardGallery.setAttribute('data-gallery-upgraded', '');
    this.refs.slideshow = /** @type {import('slideshow').Slideshow} */ (slideshow);

    Promise.all([this.#loadSlideshowComponent(), this.#waitForLazyGalleryImage(slideshow, initialSlideId)]).then(() => {
      const hydratedSlideshow = /** @type {import('slideshow').Slideshow} */ (slideshow);
      const hoveringGallery = cardGallery.matches(':hover');

      if (
        hoveringGallery &&
        cardGallery.hasAttribute('data-hover-preview-carousel') &&
        !this.#galleryInteractionStarted &&
        typeof hydratedSlideshow.select === 'function'
      ) {
        this.#hoverPreviewActivated = true;
        if (initialSlideId) {
          hydratedSlideshow.select({ id: initialSlideId }, undefined, { animate: false });
        }
      } else if (!hoveringGallery) {
        this.#resetLazyGalleryToMainSlide();
      } else if (typeof hydratedSlideshow.select === 'function' && initialSlideId) {
        hydratedSlideshow.select({ id: initialSlideId }, undefined, { animate: false });
      }

      cardGallery.removeAttribute('data-lazy-gallery-loading');
      cardGallery.setAttribute('data-lazy-gallery-ready', '');
    }).catch(() => undefined);

    return this.refs.slideshow;
  }

  #waitForLazyGalleryImage(slideshow, slideId) {
    const selectedSlide = Array.from(slideshow.querySelectorAll('slideshow-slide')).find(
      (slide) => slide.getAttribute('slide-id') === slideId
    );
    const image = selectedSlide?.querySelector('img');
    if (!(image instanceof HTMLImageElement)) return Promise.resolve();

    const decode = () => (typeof image.decode === 'function' ? image.decode().catch(() => undefined) : Promise.resolve());

    if (image.complete) {
      return decode();
    }

    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        image.removeEventListener('load', finish);
        image.removeEventListener('error', finish);
        decode().finally(resolve);
      };

      image.addEventListener('load', finish);
      image.addEventListener('error', finish);
      window.setTimeout(finish, 1600);
    });
  }

  #loadSlideshowComponent() {
    if (customElements.get('slideshow-component')) {
      return Promise.resolve();
    }

    return import('@theme/slideshow')
      .then(() => {
        return customElements.whenDefined('slideshow-component');
      })
      .catch(() => {
        return customElements.whenDefined('slideshow-component');
      });
  }

  #getLazyGalleryInitialSlide(slidesData) {
    const visibleSlides = slidesData.filter((slide) => !slide.hidden);

    return visibleSlides[1] ?? visibleSlides[0] ?? slidesData[0];
  }

  #getLazyGalleryMainSlide(slidesData) {
    const visibleSlides = slidesData.filter((slide) => !slide.hidden);

    return visibleSlides[0] ?? slidesData[0];
  }

  #getLazyGalleryHoverOrder(slidesData, initialSlideData, mainSlideData) {
    const initialSlideId = initialSlideData?.id != null ? `${initialSlideData.id}` : '';
    const mainSlideId = mainSlideData?.id != null ? `${mainSlideData.id}` : '';

    if (!initialSlideId || initialSlideId === mainSlideId) {
      return slidesData.map((slide, lazyIndex) => ({ ...slide, lazyIndex }));
    }

    const orderedSlidesData = [
      ...slidesData.filter((slide) => `${slide.id}` === initialSlideId),
      ...slidesData.filter((slide) => `${slide.id}` !== initialSlideId && `${slide.id}` !== mainSlideId),
      ...slidesData.filter((slide) => `${slide.id}` === mainSlideId),
    ];

    return orderedSlidesData.map((slide, lazyIndex) => ({ ...slide, lazyIndex }));
  }

  #resetLazyGalleryToMainSlide() {
    const cardGallery = this.#getCardGallery();
    if (!cardGallery || !cardGallery.hasAttribute('data-gallery-upgraded')) return false;

    const slideshow = this.refs.slideshow ?? cardGallery.querySelector('slideshow-component');
    if (!(slideshow instanceof HTMLElement)) return false;

    const slides = Array.from(slideshow.querySelectorAll('slideshow-slide'));
    const mainSlide =
      slides.find((slide) => slide instanceof HTMLElement && slide.hasAttribute('data-card-gallery-main-slide')) ??
      slides.find((slide) => !slide.hasAttribute('hidden')) ??
      slides[0];
    if (!(mainSlide instanceof HTMLElement)) return false;

    const slideId = mainSlide.getAttribute('slide-id');
    if (typeof this.refs.slideshow?.select === 'function' && slideId) {
      this.refs.slideshow.select({ id: slideId }, undefined, { animate: false });
    } else {
      const scroller = slideshow.querySelector('slideshow-slides');
      if (scroller instanceof HTMLElement) scroller.scrollLeft = mainSlide.offsetLeft;
    }

    return true;
  }

  /**
   * @param {HTMLElement} [cardGallery] - Source of the localized arrow labels (data-label-* attributes).
   */
  #createLazyGalleryArrows(cardGallery) {
    const arrows = document.createElement('slideshow-arrows');
    arrows.setAttribute('position', 'center');

    arrows.appendChild(this.#createLazyGalleryArrow('previous', cardGallery?.dataset.labelPrevious));
    arrows.appendChild(this.#createLazyGalleryArrow('next', cardGallery?.dataset.labelNext));

    return arrows;
  }

  /**
   * @param {'previous' | 'next'} action
   * @param {string} [label] - Localized aria-label, falls back to English.
   */
  #createLazyGalleryArrow(action, label) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = [
      'slideshow-control',
      `slideshow-control--${action}`,
      'slideshow-control--style-arrow',
      'button',
      'button-unstyled',
      'button-unstyled--transparent',
      action === 'previous' ? 'flip-x' : '',
    ]
      .filter(Boolean)
      .join(' ');
    button.setAttribute('aria-label', label || (action === 'previous' ? 'Previous slide' : 'Next slide'));
    button.setAttribute('on:click', `/${action}`);
    button.setAttribute('ref', action);
    button.innerHTML = `
      <span class="svg-wrapper icon-arrow">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path d="M13.2217 20.7372L11.7683 19.2768L16.9535 14.0937C17.3136 13.7337 17.0587 13.1181 16.5495 13.1181H1.87891V10.9276H16.5129C17.0227 10.9276 17.2773 10.3105 16.9158 9.95103L11.7367 4.80066L13.1901 3.30859L21.8789 12.0388L13.2217 20.7372Z" fill="currentColor"/>
        </svg>
      </span>
    `;

    return button;
  }

  /**
   * @param {any} slideData - Per-slide payload (id, hidden, variantImage, src, alt, aspectRatio, focalPoint).
   * @param {{ className?: string, mediaFit?: string, sizes?: string }} shared - Per-card shared payload.
   * @param {string} [selectedSlideId]
   * @param {string} [mainSlideId]
   */
  #createLazyGallerySlide(slideData, shared, selectedSlideId = '', mainSlideId = '') {
    const slide = document.createElement('slideshow-slide');
    slide.setAttribute('ref', 'slides[]');
    slide.setAttribute('aria-hidden', `${`${slideData.id}` !== selectedSlideId}`);
    slide.setAttribute('slide-id', `${slideData.id}`);
    slide.style.setProperty('view-timeline-name', `--slide-${slideData.lazyIndex ?? 0}`);
    slide.style.setProperty('--product-media-fit', shared.mediaFit || 'cover');
    if (shared.className) slide.className = shared.className;
    if (slideData.hidden) slide.hidden = true;
    if (slideData.variantImage) slide.setAttribute('variant-image', '');
    if (`${slideData.id}` === mainSlideId) slide.setAttribute('data-card-gallery-main-slide', '');

    const media = document.createElement('div');
    media.className = 'product-media';
    media.style.setProperty('--ratio', `${slideData.aspectRatio || 1}`);
    media.dataset.mediaId = `${slideData.id}`;

    const image = document.createElement('img');
    image.className = 'product-media__image';
    image.src = slideData.src;
    image.srcset = this.#buildLazyGallerySrcset(slideData.src);
    if (shared.sizes) image.sizes = shared.sizes;
    image.alt = slideData.alt || '';
    const selected = `${slideData.id}` === selectedSlideId;
    image.loading = selected ? 'eager' : 'lazy';
    image.decoding = 'async';
    image.draggable = false;
    image.fetchPriority = selected ? 'high' : 'auto';
    image.style.setProperty('--focal-point', slideData.focalPoint || 'center center');

    media.appendChild(image);
    slide.appendChild(media);

    return slide;
  }


  /**
   * Prefetches the product page HTML for browsers without Speculation Rules API.
   * In Chrome/Edge, speculation rules handle prerendering. This is a fallback
   * for Safari, Firefox, and other browsers.
   */
  #prefetchProductPage() {
    if (HTMLScriptElement.supports?.('speculationrules')) return;

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = this.productPageUrl;
    link.as = 'document';
    document.head.appendChild(link);
  }

  /**
   * Preload a single image URL with decode() for smoother display.
   * @param {string | null} url
   */
  #preloadImageUrl(url) {
    if (!url) return;
    const image = new Image();
    image.decoding = 'async';
    image.src = url;

    // Use decode() to preload and decode the image in the background
    // This prevents main thread blocking when the image is displayed
    image.decode().catch(() => {
      // Silent fail - image will load normally without pre-decoding
    });
  }

  /**
   * Preload optimal image from srcset for product cards.
   * @param {string} srcset
   */
  #preloadImageFromSrcset(srcset) {
    const entries = srcset
      .split(',')
      .map((entry) => entry.trim().split(' '))
      .map(([url, descriptor]) => ({
        url: url ?? '',
        width: parseInt(descriptor ?? '', 10) || 0,
      }))
      .filter((entry) => entry.url);

    if (!entries.length) return;

    const targetWidth = window.devicePixelRatio > 1 ? 940 : 470;
    const fallback = entries[entries.length - 1];
    const optimal = entries.find((entry) => entry.width >= targetWidth) ?? fallback;
    if (!optimal?.url) return;

    this.#preloadImageUrl(optimal.url);
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (this.#mobileStaticGalleryActive) return;

    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }

  get #mobileStaticGalleryActive() {
    return !isDesktopBreakpoint() && this.#getCardGallery()?.hasAttribute('data-mobile-static-gallery');
  }

  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;

      const cardGallery = this.#getCardGallery();
      const hoveringCard = this.matches(':hover') || cardGallery?.matches(':hover');
      if (event.detail.trigger === 'scroll' && hoveringCard) {
        this.#galleryInteractionStarted = true;
      }
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    if (this.#mobileStaticGalleryActive) return;

    this.#ensureLazyGallerySlideshow();

    const { slideshow } = this.refs;

    if (!slideshow) return;
    if (typeof slideshow.select !== 'function') return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;
    if (this.#mobileStaticGalleryActive) return;

    this.#ensureLazyGallerySlideshow();

    const { slideshow } = this.refs;

    if (!slideshow) return;
    if (typeof slideshow.select !== 'function' || typeof slideshow.next !== 'function') return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      // Immediately preload next image for continuous hover navigation
      requestAnimationFrame(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Safari can fire hover-capable pointer events without a reliable pointerType.
   * Treat classic mouseenter and empty pointerType as desktop hover, but keep touch/pen out.
   * @param {Event} event
   * @returns {boolean}
   */
  #isDesktopHoverEvent(event) {
    if (event.type === 'mouseenter' || event.type === 'mouseover') return true;

    if (typeof PointerEvent !== 'undefined' && event instanceof PointerEvent && event.pointerType) {
      return event.pointerType === 'mouse';
    }

    if (typeof PointerEvent !== 'undefined' && event instanceof PointerEvent && !event.pointerType) {
      return true;
    }

    return isDesktopBreakpoint();
  }

  /** @type {(event: PointerEvent | MouseEvent) => void} */
  #handleGalleryPointerEnter = (event) => {
    const cardGallery = this.#getEventCardGallery(event);
    if (!cardGallery) {
      return;
    }
    const isDesktopHoverEvent = this.#isDesktopHoverEvent(event);
    const mobileStaticGalleryActive = this.#mobileStaticGalleryActive;
    if (!isDesktopHoverEvent) return;
    if (mobileStaticGalleryActive) return;

    this.#ensureLazyGallerySlideshow();

    const { slideshow } = this.refs;
    const slides = slideshow?.slides ?? [];

    if (!cardGallery?.hasAttribute('data-hover-preview-carousel')) return;
    if (!slideshow || slides.length < 2) return;
    if (typeof slideshow.select !== 'function') return;
    if (this.#hoverPreviewActivated) return;
    if (this.#galleryInteractionStarted) return;

    this.#hoverPreviewActivated = true;
    this.resetVariant.cancel();
    const hoverPreviewSlideId = cardGallery.dataset.hoverPreviewSlideId;
    if (hoverPreviewSlideId) {
      slideshow.select({ id: hoverPreviewSlideId }, event, { animate: false });
    } else {
      slideshow.select(1, event, { animate: false });
    }
    requestAnimationFrame(() => this.#preloadNextPreviewImage());
  };

  #handleCardMouseLeave = () => {
    this.#hoverPreviewActivated = false;
    this.#galleryInteractionStarted = false;
    this.#previousSlideIndex = null;
    this.resetVariant.cancel();
    this.#resetImageToInitial();
  };

  /** @type {(event: PointerEvent) => void} */
  #handleSlideshowControlPointerDown = (event) => {
    if (!(event.target instanceof Element)) return;

    const control = event.target.closest('.slideshow-control');
    if (!control) return;

    const cardGallery = this.#getCardGallery();
    if (cardGallery && cardGallery.contains(control)) {
      this.#galleryInteractionStarted = true;
      this.#suppressHoverResetUntil = performance.now() + 250;
    }
  };

  #releaseDecodeLock = () => {
    this.#decodeLockActive = false;
    if (this.#decodeLockTimeoutId) {
      clearTimeout(this.#decodeLockTimeoutId);
      this.#decodeLockTimeoutId = null;
    }
  };

  /** @type {(event: MouseEvent) => void} */
  #handleSlideshowControlClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const control = event.target.closest('.slideshow-control');
    if (!control) return;

    const cardGallery = this.#getCardGallery();
    if (!cardGallery || !cardGallery.contains(control)) return;

    if (this.#decodeLockActive) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    this.#decodeLockActive = true;

    const { slideshow } = this.refs;
    const slides = slideshow?.slides ?? [];
    if (!slideshow || !slides.length) {
      this.#releaseDecodeLock();
      return;
    }

    const direction = control.classList.contains('slideshow-control--previous') ? -1 : 1;
    const currentIndex = Number.isFinite(slideshow.current) ? slideshow.current : 0;
    const targetIndex = Math.min(Math.max(currentIndex + direction, 0), slides.length - 1);
    const targetSlide = slides[targetIndex];
    const targetImage = targetSlide?.querySelector('img');

    if (targetImage?.complete) {
      this.#releaseDecodeLock();
      return;
    }

    const decodePromise =
      targetImage && typeof targetImage.decode === 'function'
        ? targetImage.decode().catch(() => undefined)
        : Promise.resolve();

    const timeoutPromise = new Promise((resolve) => {
      this.#decodeLockTimeoutId = window.setTimeout(resolve, 300);
    });

    Promise.race([decodePromise, timeoutPromise]).finally(() => {
      this.#releaseDecodeLock();
    });
  };

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;
    if (this.#mobileStaticGalleryActive) return;

    if (this.#suppressHoverResetUntil && performance.now() < this.#suppressHoverResetUntil) {
      return;
    }

    const relatedTarget = event.relatedTarget instanceof Element ? event.relatedTarget : null;
    const cardGallery = event.currentTarget instanceof Element ? event.currentTarget : this.#getCardGallery();
    if (relatedTarget && cardGallery && cardGallery.contains(relatedTarget)) {
      return;
    }

    this.#resetImageToInitial();
  }

  #resetImageToInitial() {
    if (this.#mobileStaticGalleryActive) return;
    if (this.#resetLazyGalleryToMainSlide()) return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      const initialSlide = slideshow.initialSlide;
      const slideId = initialSlide?.getAttribute('slide-id');
      if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
        slideshow.select({ id: slideId }, undefined, { animate: false });
        return;
      }
      slideshow.select(0, undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    if (this.#mobileStaticGalleryActive) return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Checks whether a target should keep its own interaction instead of card navigation.
   * @param {Element} target
   * @returns {boolean}
   */
  #isInteractiveTarget(target) {
    const interactiveElement = target.closest(
      'button, input, label, select, textarea, summary, [role="button"], [tabindex="1"], .slideshow-control, quick-add, swatches-variant-picker-component'
    );

    if (interactiveElement) return true;

    return false;
  }

  /**
   * Stores collection state before leaving the collection grid.
   * @returns {boolean}
   */
  #prepareProductNavigationState() {
    const link = this.refs.productCardLink;
    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return false;

    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('page');

    // Cache grid state for instant back-navigation recovery
    const collection = document.querySelector('product-collection');
    if (collection) {
      const grid = collection.querySelector('[ref="grid"]');
      if (grid) {
        try {
          const cacheUrl = new URL(window.location.href);
          cacheUrl.searchParams.delete('page');
          cacheUrl.hash = '';

          // Find product card nearest viewport center as scroll anchor
          const cards = grid.querySelectorAll('[data-product-id]');
          let anchorProductId = null;
          let minDistance = Infinity;
          const viewportCenter = window.scrollY + window.innerHeight / 2;
          for (const card of cards) {
            const rect = card.getBoundingClientRect();
            const cardCenter = window.scrollY + rect.top + rect.height / 2;
            const distance = Math.abs(cardCenter - viewportCenter);
            if (distance < minDistance) {
              minDistance = distance;
              anchorProductId = card.dataset.productId;
            }
          }

          // Determine how many pages are currently loaded from card data-page attrs
          const allCards = grid.querySelectorAll('[data-page]');
          let maxPage = 1;
          for (const c of allCards) {
            const p = Number(c.dataset.page);
            if (p > maxPage) maxPage = p;
          }

          const cachePayload = {
            html: grid.innerHTML,
            scrollY: window.scrollY,
            anchorProductId,
            pagesLoaded: maxPage,
            totalItems: grid.dataset.totalItems,
            lastPage: grid.dataset.lastPage,
            url: cacheUrl.pathname + cacheUrl.search,
          };

          const cacheData = JSON.stringify(cachePayload);

          // If cache exceeds ~3MB, drop HTML to avoid sessionStorage limit
          if (cacheData.length > 3_000_000) {
            delete cachePayload.html;
            sessionStorage.setItem('product-collection-cache', JSON.stringify(cachePayload));
          } else {
            sessionStorage.setItem('product-collection-cache', cacheData);
          }
        } catch (e) {
          // sessionStorage full or unavailable - bfcache will handle it
        }
      }
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    return true;
  }

  /** @type {(event: PointerEvent) => void} */
  #handleGalleryTapPointerDown = (event) => {
    if (!(event.target instanceof Element)) return;
    if (this.hasAttribute('data-no-navigation')) return;
    if (this.#isInteractiveTarget(event.target)) return;

    this.#galleryInteractionStarted = true;

    if (event.pointerType !== 'touch') return;

    this.#galleryTapIntent = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    };
  };

  /** @type {(event: PointerEvent) => void} */
  #handleGalleryTapPointerUp = (event) => {
    if (event.pointerType !== 'touch') return;
    if (!(event.target instanceof Element)) return;

    const intent = this.#galleryTapIntent;
    this.#galleryTapIntent = null;

    if (!intent || intent.pointerId !== event.pointerId) return;
    if (this.hasAttribute('data-no-navigation')) return;
    if (this.#isInteractiveTarget(event.target)) return;

    const deltaX = Math.abs(event.clientX - intent.startX);
    const deltaY = Math.abs(event.clientY - intent.startY);
    if (deltaX > TAP_SLOP_PX || deltaY > TAP_SLOP_PX) return;

    const link = this.refs.cardGalleryLink instanceof HTMLAnchorElement ? this.refs.cardGalleryLink : this.refs.productCardLink;
    if (!link.href) return;
    if (!this.#prepareProductNavigationState()) return;

    event.preventDefault();
    event.stopPropagation();

    document.addEventListener(
      'click',
      (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopImmediatePropagation();
      },
      { capture: true, once: true }
    );

    this.#navigateToURL(event, new URL(link.href));
  };

  #clearGalleryTapIntent = () => {
    this.#galleryTapIntent = null;
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    // If the click was on an interactive element, do nothing.
    if (this.#isInteractiveTarget(event.target)) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('page');

    // Cache grid state for instant back-navigation recovery
    const collection = document.querySelector('product-collection');
    if (collection) {
      const grid = collection.querySelector('[ref="grid"]');
      if (grid) {
        try {
          const cacheUrl = new URL(window.location.href);
          cacheUrl.searchParams.delete('page');
          cacheUrl.hash = '';

          // Find product card nearest viewport center as scroll anchor
          const cards = grid.querySelectorAll('[data-product-id]');
          let anchorProductId = null;
          let minDistance = Infinity;
          const viewportCenter = window.scrollY + window.innerHeight / 2;
          for (const card of cards) {
            const rect = card.getBoundingClientRect();
            const cardCenter = window.scrollY + rect.top + rect.height / 2;
            const distance = Math.abs(cardCenter - viewportCenter);
            if (distance < minDistance) {
              minDistance = distance;
              anchorProductId = card.dataset.productId;
            }
          }

          // Determine how many pages are currently loaded from card data-page attrs
          const allCards = grid.querySelectorAll('[data-page]');
          let maxPage = 1;
          for (const c of allCards) {
            const p = Number(c.dataset.page);
            if (p > maxPage) maxPage = p;
          }

          const cachePayload = {
            html: grid.innerHTML,
            scrollY: window.scrollY,
            anchorProductId,
            pagesLoaded: maxPage,
            totalItems: grid.dataset.totalItems,
            lastPage: grid.dataset.lastPage,
            url: cacheUrl.pathname + cacheUrl.search,
          };

          const cacheData = JSON.stringify(cachePayload);

          // If cache exceeds ~3MB, drop HTML to avoid sessionStorage limit
          if (cacheData.length > 3_000_000) {
            delete cachePayload.html;
            sessionStorage.setItem('product-collection-cache', JSON.stringify(cachePayload));
          } else {
            sessionStorage.setItem('product-collection-cache', cacheData);
          }
        } catch (e) {
          // sessionStorage full or unavailable — bfcache will handle it
        }
      }
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
